import { DateTime } from 'luxon';

import { FilterTagsCondition } from 'dashboard/components/TagsConditionFilter';
import { CasesFilterParams } from 'dashboard/redux/actions/cases';
import { ApiLogsFilterParams } from 'dashboard/redux/slices/api-logs';
import { WebhookEventsFilterParams } from 'dashboard/redux/slices/webhook-events';
import { ApiModel, ApiRelationship, ApiResource } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

// This should match the TableKey module in app/models/table_view.rb
export enum TableKey {
  ApiLogsIndex = 'api_logs_index',
  CasesIndex = 'cases_index',
  WebhookEventsIndex = 'wbhe_index',
}

export interface AttributeColumn {
  key: string;
  label: string;
}

export interface CasesFilterRelativeTimeSpan {
  'date-type': 'created_at' | 'resolved_at';
  interval: number;
  unit: 'days' | 'months';
}

export interface CasesAttributeFilters {
  'relative-time-span'?: CasesFilterRelativeTimeSpan;
  'case-template-ids': string[];
  'status-names': string[];
  'team-assignee-ids': string[];
  'assignee-ids': string[];
  'tag-conditions': FilterTagsCondition[][];
  'case-queue-ids'?: string[];
}

export type ApiLogsAttributeFilters = Pick<ApiLogsFilterParams, 'status' | 'methods' | 'apiKeyIds'>;
export type WebhookEventsAttributeFilters = Pick<WebhookEventsFilterParams, 'status'>;

interface SortParams {
  sort: string;
  'should-sort-descending': boolean;
}

export interface TableViewConfiguration<AttributeFilters> {
  columns: AttributeColumn[];
  filters: AttributeFilters;
  sort?: SortParams;
}

export enum TableViewStatus {
  Active = 'active',
  Archived = 'archived',
}

export interface TableViewAttributes<AttributeFilters> {
  name: string;
  status: TableViewStatus;
  configuration: TableViewConfiguration<AttributeFilters>;
  'table-key': TableKey;
}

export interface TableViewRelationships {
  organization: ApiRelationship;
}

export type TableView<AttributeFilters> = ApiResource<
  TableViewAttributes<AttributeFilters>,
  TableViewRelationships,
  ApiModel.TableView
>;

export type ApiLogsTableView = TableView<ApiLogsAttributeFilters>;
export type CasesTableView = TableView<CasesAttributeFilters>;
export type WebhookEventsTableView = TableView<WebhookEventsAttributeFilters>;

export const generateFilterFromRelativeTimeSpan = (
  relativeTimeSpan: CasesFilterRelativeTimeSpan,
) => {
  const filters: CasesFilterParams = {};
  if (relativeTimeSpan) {
    filters.dateType = relativeTimeSpan['date-type'];

    const { unit, interval } = relativeTimeSpan;
    let startAt: DateTime;
    const endAt = DateTime.local().endOf('day');
    switch (unit) {
      //  Note: The interval for 'months' works in a specific way:
      //  * { interval: 1: unit: 'months' } represents the current month, 'This month'.
      case 'months': {
        const offset = 0;
        const referencedMonth = endAt.minus({ months: offset });

        // Calculate start time
        startAt = referencedMonth.set({ day: 1 }).startOf('day');

        break;
      }
      // Compute offset for 'days' unit
      case 'days': {
        startAt = endAt.minus({ days: interval }).startOf('day');
        break;
      }
      default:
        assertNever(unit);
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    filters.startDate = startAt.toISO();
    filters.endDate = endAt.toISO();
  } else {
    filters.startDate = undefined;
    filters.endDate = undefined;
  }
  return filters;
};

export const generateCaseFiltersFromTableView = (filterConfiguration: CasesAttributeFilters) => {
  if (!filterConfiguration) {
    return {};
  }

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const filters = generateFilterFromRelativeTimeSpan(filterConfiguration['relative-time-span']);

  filters.caseStateNames = filterConfiguration['status-names'];
  filters.caseTemplateIds = filterConfiguration['case-template-ids'];
  filters.assigneeIds = filterConfiguration['assignee-ids'];
  filters.teamAssigneeIds = filterConfiguration['team-assignee-ids'];
  filters.tagsConditions = filterConfiguration['tag-conditions'];
  filters.caseQueueIds = filterConfiguration['case-queue-ids'] || [];

  return filters;
};
